export interface TimezoneOption {
  id: string;
  value: string;
  text: string;
  offset: string;
}

// Common timezones as fallback if Intl.supportedValuesOf isn't available
const COMMON_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Madrid",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Kolkata",
  "Asia/Dubai",
  "Australia/Sydney",
  "Australia/Melbourne",
];

// Intl.supportedValuesOf is not available in all browsers, so we use a fallback
export function getAvailableTimezones(): string[] {
  try {
    if ("supportedValuesOf" in Intl) {
      return Intl.supportedValuesOf("timeZone");
    }
  } catch (error) {
    console.warn(
      "Intl.supportedValuesOf not available, using fallback timezones",
    );
  }
  return COMMON_TIMEZONES;
}

export function getTimezoneWithOffset(timezone: string): TimezoneOption {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "shortOffset",
  });
  const parts = formatter.formatToParts(now);
  const offset =
    parts.find((part) => part.type === "timeZoneName")?.value || "";

  const formattedOffset = offset.replace(
    /GMT([+-])(\d+)(?::(\d+))?/,
    (_, sign, hours, minutes) => {
      const paddedHours = hours.padStart(2, "0");
      const paddedMinutes = (minutes || "00").padStart(2, "0");
      return `GMT${sign}${paddedHours}:${paddedMinutes}`;
    },
  );

  return {
    id: timezone,
    value: timezone,
    text: `(${formattedOffset}) ${timezone.replace(/_/g, " ")}`,
    offset,
  };
}

export function getAllTimezonesWithOffsets(): TimezoneOption[] {
  const allTimezones = getAvailableTimezones().map((timezone) =>
    getTimezoneWithOffset(timezone),
  );

  const seen = new Map<string, TimezoneOption>();

  /**
   * Only keep one timezone per country and offset to avoid large number of timezones
   * Prioritize timezones from COMMON_TIMEZONES
   */
  for (const tz of allTimezones) {
    const country = tz.value.split("/")[0];
    const key = `${country}_${tz.offset}`;

    const existing = seen.get(key);
    if (!existing) {
      seen.set(key, tz);
    } else if (
      COMMON_TIMEZONES.includes(tz.value) &&
      !COMMON_TIMEZONES.includes(existing.value)
    ) {
      seen.set(key, tz);
    }
  }

  const timezones = Array.from(seen.values());

  timezones.sort((a, b) => {
    const parseOffset = (offset: string): number => {
      if (offset === "GMT") return 0;
      const match = offset.match(/GMT([+-])(\d+)/);
      if (!match) return 0;
      const sign = match[1] === "-" ? -1 : 1;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const hours = parseInt(match[2], 10);
      return sign * hours;
    };

    const offsetA = parseOffset(a.offset);
    const offsetB = parseOffset(b.offset);

    if (offsetA === offsetB) {
      return a.value.localeCompare(b.value);
    }
    return offsetA - offsetB;
  });

  return timezones;
}

export function getTimezoneOffset(timezone: string): string {
  return getTimezoneWithOffset(timezone).offset;
}

export function formatTimezoneDisplay(timezone: string): string {
  return timezone.replace(/_/g, " ");
}
